<script setup lang="ts">
import { Copy, Plus, Trash2 } from 'lucide-vue-next'

interface TimeSlot {
  startHour: string
  endHour: string
}

interface DaySchedule {
  weekday: number
  slots: TimeSlot[]
}

interface OpeningHoursSlot {
  id: number
  weekdays: number[]
  startTime: string
  endTime: string
}

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const
const DEFAULT_START = '09:00'
const DEFAULT_END = '18:00'
const ISO_WEEK_SAMPLE_MONDAY = new Date(2024, 0, 1)

const days = defineModel<DaySchedule[]>('days', { required: true })

const props = defineProps<{
  disabled?: boolean
}>()

const { t, locale } = useI18n()

const timeSlots = ref<OpeningHoursSlot[]>([])
const syncingFromDays = ref(false)
const lastAppliedSignature = ref('')
const lastClickedDayIndexBySlot = ref<Record<number, number | null>>({})

const nextSlotId = () => (timeSlots.value[timeSlots.value.length - 1]?.id ?? 0) + 1

const sampleDateForWeekday = (weekday: number) => {
  const date = new Date(ISO_WEEK_SAMPLE_MONDAY)
  date.setDate(ISO_WEEK_SAMPLE_MONDAY.getDate() + weekday - 1)
  return date
}

const weekdayFormatterNarrow = computed(() => new Intl.DateTimeFormat(locale.value, { weekday: 'narrow' }))

const dayOptions = computed(() =>
  WEEKDAYS.map((weekday) => ({
    value: weekday,
    label: weekdayFormatterNarrow.value.format(sampleDateForWeekday(weekday))
  }))
)

const daysSignature = (schedule: DaySchedule[]) =>
  JSON.stringify(
    schedule.map((day) => ({
      weekday: day.weekday,
      slots: day.slots.map((slot) => `${slot.startHour.slice(0, 5)}-${slot.endHour.slice(0, 5)}`)
    }))
  )

const daysToSlots = (schedule: DaySchedule[]): OpeningHoursSlot[] => {
  const groups = new Map<string, { startTime: string; endTime: string; weekdays: Set<number> }>()

  schedule.forEach((day) => {
    day.slots.forEach((slot) => {
      const startTime = slot.startHour.slice(0, 5)
      const endTime = slot.endHour.slice(0, 5)
      const key = `${startTime}-${endTime}`
      const existing = groups.get(key)

      if (existing) {
        existing.weekdays.add(day.weekday)
        return
      }

      groups.set(key, {
        startTime,
        endTime,
        weekdays: new Set([day.weekday])
      })
    })
  })

  const slots = [...groups.values()].map((group, index) => ({
    id: index + 1,
    weekdays: WEEKDAYS.filter((weekday) => group.weekdays.has(weekday)),
    startTime: group.startTime,
    endTime: group.endTime
  }))

  return slots.length > 0
    ? slots
    : [
        {
          id: 1,
          weekdays: [],
          startTime: DEFAULT_START,
          endTime: DEFAULT_END
        }
      ]
}

const applySlotsToDays = (slots: OpeningHoursSlot[]) => {
  days.value.forEach((day) => {
    day.slots = []
  })

  slots.forEach((slot) => {
    if (!slot.startTime && !slot.endTime) {
      return
    }

    slot.weekdays.forEach((weekday) => {
      const day = days.value.find((entry) => entry.weekday === weekday)
      day?.slots.push({
        startHour: slot.startTime,
        endHour: slot.endTime
      })
    })
  })
}

const addTimeSlot = () => {
  timeSlots.value.push({
    id: nextSlotId(),
    weekdays: [],
    startTime: '',
    endTime: ''
  })
}

const duplicateTimeSlot = (slotId: number) => {
  const index = timeSlots.value.findIndex((slot) => slot.id === slotId)
  const source = timeSlots.value[index]
  if (!source) {
    return
  }

  timeSlots.value.splice(index + 1, 0, {
    id: nextSlotId(),
    weekdays: [...source.weekdays],
    startTime: source.startTime,
    endTime: source.endTime
  })
}

const removeTimeSlot = (slotId: number) => {
  if (timeSlots.value.length <= 1) {
    return
  }

  timeSlots.value = timeSlots.value.filter((slot) => slot.id !== slotId)
}

const hasInvalidTimeRange = computed(() =>
  timeSlots.value.some((slot) => slot.startTime && slot.endTime && slot.endTime <= slot.startTime)
)

const toggleDayForSlot = (slotId: number, weekday: number, event?: MouseEvent) => {
  const slot = timeSlots.value.find((entry) => entry.id === slotId)
  if (!slot) {
    return
  }

  const clickedIndex = WEEKDAYS.findIndex((day) => day === weekday)
  if (clickedIndex === -1) {
    return
  }

  const lastIndex = lastClickedDayIndexBySlot.value[slotId] ?? null

  if (event?.shiftKey && lastIndex !== null && lastIndex !== clickedIndex) {
    const start = Math.min(lastIndex, clickedIndex)
    const end = Math.max(lastIndex, clickedIndex)
    const rangeDays = WEEKDAYS.slice(start, end + 1)
    slot.weekdays = [...new Set([...slot.weekdays, ...rangeDays])]
  } else if (slot.weekdays.includes(weekday)) {
    slot.weekdays = slot.weekdays.filter((day) => day !== weekday)
  } else {
    slot.weekdays = [...slot.weekdays, weekday]
  }

  lastClickedDayIndexBySlot.value[slotId] = clickedIndex
}

watch(
  () => daysSignature(days.value),
  (signature) => {
    if (signature === lastAppliedSignature.value) {
      return
    }

    syncingFromDays.value = true
    timeSlots.value = daysToSlots(days.value)
    lastAppliedSignature.value = signature
    nextTick(() => {
      syncingFromDays.value = false
    })
  },
  { immediate: true }
)

watch(
  timeSlots,
  (slots) => {
    if (syncingFromDays.value || props.disabled) {
      return
    }

    applySlotsToDays(slots)
    lastAppliedSignature.value = daysSignature(days.value)
  },
  { deep: true }
)
</script>

<template>
  <div class="mt-4 flex w-full flex-col items-start gap-3 self-stretch">
    <div class="mb-2 text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
      {{ t('pages.settings.storesPage.openingHours.timeSlots') }}
    </div>

    <div class="w-full space-y-3">
      <div
        v-for="slot in timeSlots"
        :key="slot.id"
        class="flex w-full flex-wrap items-center gap-x-4 gap-y-3 rounded-xl border border-border bg-white p-3"
      >
        <div class="flex min-w-0 flex-wrap items-center gap-3">
          <span class="shrink-0 text-sm text-foreground">
            {{ t('pages.settings.storesPage.openingHours.activeDays') }}
          </span>
          <div class="inline-flex flex-wrap items-center gap-2">
            <Button
              v-for="day in dayOptions"
              :key="day.value"
              type="button"
              :variant="slot.weekdays.includes(day.value) ? 'default-primary' : 'outline'"
              size="icon"
              :disabled="disabled"
              @click="(e) => !disabled && toggleDayForSlot(slot.id, day.value, e)"
            >
              {{ day.label }}
            </Button>
          </div>
        </div>

        <div class="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <span class="shrink-0 text-sm text-foreground">
            {{ t('pages.settings.storesPage.openingHours.hours') }}
          </span>
          <div class="flex min-w-0 flex-wrap items-center gap-2">
            <span class="sr-only">{{ t('pages.settings.storesPage.openingHours.from') }}</span>
            <div class="w-28 sm:w-32">
              <TimeSelect v-model="slot.startTime" :disabled="disabled" />
            </div>
            <span class="pl-4 text-sm text-muted-foreground" aria-hidden="true">-</span>
            <span class="sr-only">{{ t('pages.settings.storesPage.openingHours.to') }}</span>
            <div class="w-28 sm:w-32">
              <TimeSelect v-model="slot.endTime" :disabled="disabled" />
            </div>
          </div>
        </div>

        <div v-if="!disabled" class="ml-auto flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            class="group h-6 w-6 min-w-6 p-0 hover:bg-accent"
            :aria-label="t('pages.settings.storesPage.openingHours.duplicateSlot')"
            @click="duplicateTimeSlot(slot.id)"
          >
            <Copy class="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
          </Button>
          <Button
            v-if="timeSlots.length > 1"
            type="button"
            variant="ghost"
            class="group h-6 w-6 min-w-6 p-0 hover:bg-accent"
            :aria-label="t('pages.settings.storesPage.openingHours.removeSlot')"
            @click="removeTimeSlot(slot.id)"
          >
            <Trash2 class="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
          </Button>
        </div>
      </div>
    </div>

    <p v-if="hasInvalidTimeRange" class="text-xs text-red-500">
      {{ t('pages.settings.storesPage.openingHours.notifications.invalidRange') }}
    </p>

    <Button v-if="!disabled" type="button" variant="outline" class="mt-2 w-full" @click="addTimeSlot">
      <Plus />
      {{ t('pages.settings.storesPage.openingHours.addSlot') }}
    </Button>
  </div>
</template>
